import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import emailjs from "emailjs-com";
import styled from "styled-components";
import { HiUser, HiMail, HiPhone, HiHome, HiAcademicCap } from "react-icons/hi";
import { supabase } from "../../supabaseClient";

const Payment = () => {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [billingAddress, setBillingAddress] = useState("");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [price, setPrice] = useState(0);
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // On mount, check for course in location.state or fetch available courses
  useEffect(() => {
    window.scrollTo(0, 0);
    if (location.state && location.state.course) {
      setSelectedCourse(location.state.course);
      setPrice(location.state.course.price);
      setLoadingCourses(false);
    } else {
      fetchAvailableCourses();
    }
  }, [location.state]);

  const fetchAvailableCourses = async () => {
    setLoadingCourses(true);
    const { data, error } = await supabase
      .from("courses")
      .select("id, title, price")
      .eq("available", true)
      .order("position");
    if (!error && data) {
      setCourses(data);
    }
    setLoadingCourses(false);
  };

  const handleCourseChange = (event) => {
    const courseId = event.target.value;
    const course = courses.find((c) => String(c.id) === String(courseId));
    setSelectedCourse(course);
    setPrice(course ? course.price : 0);
  };

  const handlePayment = async () => {
    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: price * 100,
      currency: "INR",
      name: "Smart Stock Investor",
      description: "Test Transaction",
      handler: async function (response) {
        try {
          await emailjs.send(
            process.env.REACT_APP_SERVICE_ID,
            process.env.REACT_APP_TEMPLATE_ID,
            {
              to_name: name,
              to_mobile: mobile,
              to_billing_address: billingAddress,
              to_email: email,
              payment_id: response.razorpay_payment_id,
              to_selected_course: selectedCourse ? selectedCourse.title : "",
            },
            process.env.REACT_APP_USER_ID
          );
        } catch (error) {
          console.error("Error sending email:", error);
        }
        navigate(`/success?payment_id=${response.razorpay_payment_id}`);
      },
      prefill: {
        name: name,
        email: email,
        contact: mobile,
      },
      notes: {
        address: billingAddress,
      },
      theme: {
        color: "#3399cc",
      },
    };
    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  return (
    <Wrapper id="contact">
      <div className="flex mt-20 justify-center items-center min-h-screen bg-gradient-to-br from-blue-50 to-white py-10">
        <CardContainer>
          {/* Logo/Illustration Placeholder */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-3xl font-bold shadow-md">
              <img src={require("../../assets/img/clients/logo.png")} />
            </div>
          </div>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-extrabold text-gray-800 mb-1">
              Complete Your Payment
            </h2>
            <p className="text-gray-500 text-sm">
              Join the{" "}
              <span className="font-semibold text-blue-600">
                Smart Stock Investor
              </span>{" "}
              course
            </p>
          </div>
          {/* Summary Section */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HiAcademicCap className="text-blue-400 text-xl" />
              <div>
                <div className="text-gray-700 font-semibold">
                  {selectedCourse ? selectedCourse.title : "Select a course"}
                </div>
                <div className="text-xs text-gray-500">One-time payment</div>
              </div>
            </div>
            <div className="text-xl font-bold text-green-600">₹{price}</div>
          </div>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handlePayment();
            }}
          >
            <Label>Full Name</Label>
            <InputWrapper>
              <HiUser className="input-icon" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Enter your name"
              />
            </InputWrapper>
            <Label>Mobile Number</Label>
            <InputWrapper>
              <HiPhone className="input-icon" />
              <input
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                required
                placeholder="Enter your mobile number"
              />
            </InputWrapper>
            <Label>Email</Label>
            <InputWrapper>
              <HiMail className="input-icon" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
              />
            </InputWrapper>
            {/* Only show course dropdown if not locked-in from previous page */}
            {!selectedCourse || !location.state?.course ? (
              <>
                <Label>Choose Course</Label>
                <InputWrapper>
                  <HiAcademicCap className="input-icon" />
                  {loadingCourses ? (
                    <select disabled>
                      <option>Loading...</option>
                    </select>
                  ) : (
                    <select
                      value={selectedCourse ? selectedCourse.id : ""}
                      onChange={handleCourseChange}
                      required
                    >
                      <option value="" disabled>
                        Select a course
                      </option>
                      {courses.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.title}
                        </option>
                      ))}
                    </select>
                  )}
                </InputWrapper>
              </>
            ) : null}
            <Label>Billing Address</Label>
            <InputWrapper>
              <HiHome className="input-icon" />
              <textarea
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
                required
                placeholder="Enter your billing address"
              />
            </InputWrapper>
            <PayButton type="submit">Pay ₹{price}</PayButton>
          </Form>
        </CardContainer>
      </div>
    </Wrapper>
  );
};

export default Payment;

const Wrapper = styled.section`
  width: 100%;
`;

const CardContainer = styled.div`
  background: #fff;
  border-radius: 1.5rem;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.12);
  padding: 2.5rem 2rem 2rem 2rem;
  max-width: 420px;
  width: 100%;
  margin: 0 auto;
  @media (min-width: 1024px) {
    max-width: 700px;
    padding: 3rem 3.5rem 3rem 3.5rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
`;

const Label = styled.label`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.2rem;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  input,
  select,
  textarea {
    width: 100%;
    padding: 0.75rem 0.75rem 0.75rem 2.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    background: #f9fafb;
    font-size: 1rem;
    color: #111827;
    outline: none;
    transition: border 0.2s;
    &:focus {
      border-color: #2563eb;
    }
  }
  textarea {
    min-height: 80px;
    resize: none;
  }
  .input-icon {
    position: absolute;
    left: 0.75rem;
    color: #9ca3af;
    font-size: 1.25rem;
    pointer-events: none;
  }
`;

const PayButton = styled.button`
  margin-top: 1.5rem;
  width: 100%;
  padding: 0.9rem 0;
  background: linear-gradient(90deg, #22c55e 0%, #2563eb 100%);
  color: #fff;
  font-size: 1.1rem;
  font-weight: 700;
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 4px 14px 0 rgba(34, 197, 94, 0.15);
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  &:hover {
    background: linear-gradient(90deg, #16a34a 0%, #1d4ed8 100%);
    box-shadow: 0 6px 20px 0 rgba(37, 99, 235, 0.18);
  }
`;
